import { KDrawer, KText } from '@/ui-library/components';
import {
  Flex,
  Box,
  Button,
  UseDisclosureProps,
  useDisclosure,
} from '@chakra-ui/react';
import { KSteps } from '@/ui-library/components/molecules';
import type { KStepItem } from '@/ui-library/components/molecules/KSteps';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AiCandidateAttributes } from './AiCandidateAttributes';
import { AutoRejectSection } from './AutoRejectSection';
import { AutoRejectEmailSection } from './AutoRejectEmailSection';
import { useAutoRejectEmail } from './useAutoRejectEmail';
import { AiScoringDrawerFooter } from './AiScoringDrawerFooter';
import { AiScoreTesting } from '../AiScoreTesting';
import { aiScoringResponseType, aiScoringApi } from '../api/aiScoring.api';
import { useAiScoringAttributes } from '../hooks/useAiScoringAttributes';
import { useGetAiScoringMutations } from '../AiScoringSection/useGetAiScoringMutations';
import { useAiScoringDrawer } from '../hooks/useAiScoringDrawer';
import { aiScoreTestApi } from '../AiScoreTesting/api/aiScoreTest.api';
import { AiScoringRerunModal } from './AiScoringRerunModal';
import { useApplicantCount } from '../hooks/useApplicantCount';
import { AiICP } from './AiICP';
import { AiScoringConstants } from '../aiScoringConstants';
import { ModalWithForm } from '@/components/ats';
import { ScreenerInfoContent } from './ScreenerInfoContent';
import { useKFlags } from '@/hooks/use-k-flags';
import { useShowToast } from '@/hooks/useShowToast';
import { aiScoringKeys } from '@/api/aiScoring/aiScoringKeys';

const {
  STEP_ICP,
  STEP_ATTRIBUTES,
  STEP_TESTING,
  ICP_MIN_LENGTH,
  MIN_JOB_DESCRIPTION_LENGTH,
} = AiScoringConstants;

const SAVE_SETTINGS_ERROR = 'Unable to save Scoring agent settings';

export const AiScoringDrawer = ({
  drawerDisclosure,
  aiScoringData,
  jobData,
  isFromJobPipeline = false,
  onSuccessfulUpdate,
  initialView,
}: {
  drawerDisclosure: UseDisclosureProps;
  aiScoringData: aiScoringResponseType['data']['ai_scoring'];
  jobData: {
    id: string;
    description?: { text: string | null };
    status: string;
    job_status: string;
  };
  isFromJobPipeline?: boolean;
  onSuccessfulUpdate?: () => void;
  initialView?: 'info' | 'setup';
}) => {
  const { isOpen = true, onClose } = drawerDisclosure ?? {};
  const [activeStep, setActiveStep] = useState(STEP_ICP);
  const [currentView, setCurrentView] = useState<'info' | 'setup'>(
    initialView ?? 'setup'
  );

  const { feJobTeammates, feAiAutoRejectEmail } = useKFlags();
  const queryClient = useQueryClient();
  const toast = useShowToast();
  const { shouldRetrigger } = useApplicantCount(jobData);
  const cancelBtnDisclosure = useDisclosure();

  // Auto-reject state — single source of truth shared by the setup wizard
  // ("Verify attributes" step) and the "Edit Scoring agent" info view.
  const initialAutoReject = aiScoringData?.auto_reject ?? false;
  const initialAutoRejectThreshold =
    aiScoringData?.auto_reject_threshold ??
    AiScoringConstants.DEFAULT_AUTO_REJECT_THRESHOLD;
  const [autoReject, setAutoReject] = useState(initialAutoReject);
  const [autoRejectThreshold, setAutoRejectThreshold] = useState(
    initialAutoRejectThreshold
  );

  const isAutoRejectDirty =
    autoReject !== initialAutoReject ||
    autoRejectThreshold !== initialAutoRejectThreshold;

  // Email-island dirtiness, reported up from AutoRejectEmailSection so the
  // Save / Test Scoring agent buttons react to changes in the send-email
  // checkbox, template selection, or scheduling options.
  const [autoRejectEmailDirty, setAutoRejectEmailDirty] = useState(false);
  const isAutoRejectOrEmailDirty = isAutoRejectDirty || autoRejectEmailDirty;

  // Rejection-email island (ENG-3114): formRef the section writes to, restore
  // values from the persisted config, and the save-payload builder.
  const {
    formRef: autoRejectEmailFormRef,
    initialValues: autoRejectEmailInitialValues,
    buildEmailSettings: buildAutoRejectEmailSettings,
    validateEmailConfig: validateAutoRejectEmail,
  } = useAutoRejectEmail(aiScoringData);

  const updateAiScoringSettingsMutation = useMutation({
    mutationFn: async (values: {
      autoReject: boolean;
      autoRejectThreshold: number;
    }) => {
      return aiScoringApi.updateAiScoringSettings(jobData.id, {
        enabled: true,
        auto_reject: values.autoReject,
        auto_reject_threshold: values.autoRejectThreshold,
        ...buildAutoRejectEmailSettings(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: aiScoringKeys.detail(jobData?.id),
      });
      onSuccessfulUpdate?.();
      onClose?.();
    },
    onError: () => {
      toast({ title: SAVE_SETTINGS_ERROR, status: 'error' });
    },
  });

  // Blocks a save (with an error toast) when the rejection email is enabled
  // but incomplete. Every path that persists the email settings goes through
  // this guard.
  const validateEmailOrToast = useCallback(() => {
    const emailError = validateAutoRejectEmail();
    if (emailError) {
      toast({ title: emailError, status: 'error' });
      return false;
    }
    return true;
  }, [validateAutoRejectEmail, toast]);

  // Persist auto-reject settings for the setup wizard save flow, where
  // attributes and settings are saved through separate endpoints. Resolves
  // false (after toasting) when validation or the request fails so callers
  // abort by early-returning — throwing here surfaced every blocked save as
  // an unhandled rejection in Sentry (CORE-WEB-6B2).
  const persistAutoRejectIfChanged = useCallback(async (): Promise<boolean> => {
    const emailDirty = autoRejectEmailFormRef.current?.dirty ?? false;
    if (!isAutoRejectDirty && !emailDirty) return true;
    if (!validateEmailOrToast()) return false;
    try {
      await aiScoringApi.updateAiScoringSettings(jobData.id, {
        enabled: true,
        auto_reject: autoReject,
        auto_reject_threshold: autoRejectThreshold,
        ...buildAutoRejectEmailSettings(),
      });
      return true;
    } catch {
      toast({ title: SAVE_SETTINGS_ERROR, status: 'error' });
      return false;
    }
  }, [
    isAutoRejectDirty,
    jobData.id,
    autoReject,
    autoRejectThreshold,
    autoRejectEmailFormRef,
    buildAutoRejectEmailSettings,
    validateEmailOrToast,
    toast,
  ]);

  // Destructure the stable `mutate` fn so it can be a safe useCallback dep.
  const { mutate: mutateAiScoringSettings } = updateAiScoringSettingsMutation;

  const handleInfoSave = useCallback(() => {
    if (!validateEmailOrToast()) return;
    mutateAiScoringSettings({ autoReject, autoRejectThreshold });
  }, [
    mutateAiScoringSettings,
    autoReject,
    autoRejectThreshold,
    validateEmailOrToast,
  ]);

  const handleInfoCancel = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Pencil in the info view opens the existing attributes editor (setup
  // wizard), mirroring the old AI scoring UI.
  const handleEditAttributes = useCallback(() => {
    setCurrentView('setup');
    setActiveStep(STEP_ATTRIBUTES);
  }, []);

  // Track AI scoring test session state for auto-generation and cleanup
  const [icpData, setIcpData] = useState<{
    data: string;
    categories: string[];
    isModified: boolean;
  }>({
    data: '',
    categories: [],
    isModified: false,
  });
  const [testSessionKey, setTestSessionKey] = useState<string | null>(null);
  const [isAiTestLoading, setIsAiTestLoading] = useState(false);

  // Derived state - no need for separate boolean state
  const hasTestSession = !!testSessionKey;

  // Modal state for retrigger confirmation - consolidated for better management
  const [retriggerModal, setRetriggerModal] = useState<{
    isOpen: boolean;
    actionType: 'save' | 'skipAndSave' | null;
    loadingAction: 'saveWithoutRetrigger' | 'saveAndRetrigger' | null;
  }>({
    isOpen: false,
    actionType: null,
    loadingAction: null,
  });

  // Get generated attributes from the ICP generation process
  const {
    generatedAttributes,
    generatedCategories,
    generatedIcp,
    // saveIcpMutation, // Commented out - can be uncommented in future
    generateIcpIdMutation,
    getIcpCategoriesMutation,
    generateAttributesMutation,
    isLoading: isIcpLoading,
    isIcpGenerating,
    hasAttemptedGeneration,
  } = useGetAiScoringMutations({
    jobData: { id: jobData.id },
    setActiveStep,
  });

  // Use refs to track whether ICP generation has been attempted to prevent infinite loops
  const icpGenerationAttemptedRef = useRef(false);
  const lastJobDescriptionRef = useRef<string | null>(null);
  // Track if initial navigation has already been done (only once on mount)
  const hasInitialNavigationRef = useRef(false);

  // Handle automatic ICP generation when drawer opens
  useEffect(() => {
    if (isOpen) {
      const jobDescriptionText = jobData?.description?.text;

      const isJobDescriptionLongEnough =
        jobDescriptionText?.trim().length >= MIN_JOB_DESCRIPTION_LENGTH;

      // NEW REQUIREMENT: even if icp exists, we should regenerate it if the job description changes but the attributes are not saved
      // const { icp } = aiScoringData ?? {};
      // if (icp) {
      //   return;
      // }

      // Reset generation attempt flag if job description changes
      if (lastJobDescriptionRef.current !== jobDescriptionText) {
        icpGenerationAttemptedRef.current = false;
        lastJobDescriptionRef.current = jobDescriptionText;
      }

      const savedAttributes = aiScoringData?.attributes ?? [];

      // Generate ICP from job description if:
      // 1. Job description exists and has text content
      // 2. No attributes are saved yet
      // 3. Generation hasn't been attempted for this job description
      const shouldGenerateIcp =
        jobDescriptionText &&
        isJobDescriptionLongEnough &&
        savedAttributes?.length === 0 &&
        !icpGenerationAttemptedRef.current;

      if (shouldGenerateIcp) {
        icpGenerationAttemptedRef.current = true;
        generateIcpIdMutation.mutate(jobDescriptionText);
      }
    } else {
      // Reset generation attempt flag when drawer closes
      icpGenerationAttemptedRef.current = false;
      lastJobDescriptionRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, aiScoringData?.attributes, jobData?.description?.text]);

  // Create a ref to hold the validation complete callback
  // eslint-disable-next-line no-unused-vars
  const validationCompleteRef = useRef<((result: any) => void) | null>(null);

  // Memoize initialAttributes to prevent unnecessary recalculations
  const initialAttributes = useMemo(() => {
    // If we have generated attributes, use them
    if (generatedAttributes?.length > 0) {
      return generatedAttributes;
    }

    // If we've attempted generation but have no results, don't fall back to saved attributes
    // This prevents showing old attributes when generation fails
    if (hasAttemptedGeneration) {
      return [];
    }

    // Otherwise, use saved attributes from API as fallback
    return aiScoringData?.attributes ?? [];
  }, [generatedAttributes, aiScoringData?.attributes, hasAttemptedGeneration]);

  // Track when auto-navigation should be allowed (only after ICP verification)

  // Use the centralized attributes hook with both existing and generated attributes
  const {
    attributes,
    isDirty,
    isValid,
    isLoading: attributesLoading,
    handleAttributeChange,
    handleAttributeBlur,
    handleAttributeDelete,
    handleAttributeAdd,
    handleAttributeReorder,
    saveAttributes,
    resetDirtyState,
    hasValidatedButNotSaved,
    setValidatedButNotSaved,
    interactedAttributes,
    handleAttributeAutoFocus,
  } = useAiScoringAttributes({
    initialAttributes,
    jobId: jobData.id,
    onValidationComplete: (result) => {
      if (validationCompleteRef.current) {
        validationCompleteRef.current(result);
      }
    },
  });

  // Use the drawer hook to manage drawer state and actions
  const {
    footerIsLoading,
    footerLoadingKey,
    hasUnsavedGeneratedAttributes,
    shouldRerunAiTest,
    setShouldRerunAiTest,
    handleStepChange,
    handleCancel,
    handleBack,
    handleSave,
    handleSkipAndSave,
    handleVerifyIcp: originalHandleVerifyIcp,
    handleVerifyAttributes,
    handleValidationComplete,
  } = useAiScoringDrawer({
    icpData,
    setIcpData,
    getIcpCategoriesMutation,
    attributes,
    isDirty,
    isValid,
    saveAttributes,
    resetDirtyState,
    attributesLoading,
    onClose,
    hasSession: hasTestSession,
    jobData,
    isFromJobPipeline,
    hasValidatedButNotSaved,
    // saveIcpMutation, // Commented out - can be uncommented in future
    isIcpLoading,
    activeStep,
    setActiveStep,
    generateAttributesMutation,
    generatedAttributes,
    onSuccessfulUpdate,
  });

  // Create a wrapped version of handleVerifyIcp that enables auto-navigation
  const handleVerifyIcp = useCallback(async () => {
    await originalHandleVerifyIcp();
  }, [originalHandleVerifyIcp]);

  // Set the validation complete callback
  useEffect(() => {
    validationCompleteRef.current = handleValidationComplete;
  }, [handleValidationComplete]);

  // AI scoring test session cleanup when drawer is closed
  useEffect(() => {
    return () => {
      // Cleanup active test session when drawer is unmounted
      // Use refs to get current values instead of closure values
      const currentSessionKey = testSessionKey;
      const hasCurrentSession = hasTestSession;

      if (currentSessionKey && hasCurrentSession) {
        aiScoreTestApi
          .deleteTestSession(jobData.id, currentSessionKey)
          .catch((error) => {
            console.error('Error cleaning up AI scoring test session:', error);
            // Don't show error toast as this is cleanup - session might already be deleted
          });
      }
    };
  }, [testSessionKey, hasTestSession, jobData.id]); // Add dependencies to ensure latest values

  // Reset loading states when drawer closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all loading states when drawer closes
      setRetriggerModal({
        isOpen: false,
        actionType: null,
        loadingAction: null,
      });
    }
  }, [isOpen]);

  // Create a ref for the attributes list to maintain compatibility with existing AiCandidateAttributes
  const attributesListRef = useRef<{
    getAttributes: () => any[];
    saveAttributes?: () => Promise<any>;
    isLoading: boolean;
    dirty: boolean;
    isValid: boolean;
  }>({
    getAttributes: () => attributes,
    saveAttributes: saveAttributes,
    isLoading: attributesLoading,
    dirty: isDirty,
    isValid: isValid,
  });

  // Determine if it's edit mode based on whether aiScoringData has attributes
  const isEditMode = Boolean(aiScoringData?.attributes?.length > 0);

  // Initialize ICP data when drawer opens - reset with fresh data from aiScoringData
  useEffect(() => {
    if (isOpen) {
      // Prioritize fresh generatedIcp if it exists (from recent save), otherwise use aiScoringData
      const icpText = generatedIcp ?? aiScoringData?.icp ?? '';
      const categories = generatedCategories ?? aiScoringData?.categories ?? [];

      // Only initialize when there's no user content yet or
      // when newly generated ICP arrives and we're still showing the previously saved ICP
      // Avoid overwriting locally edited ICP when navigating between steps
      const hasNoUserContent =
        !icpData?.data || icpData.data.trim().length === 0;
      const isStillShowingSavedIcp =
        icpData?.data === (aiScoringData?.icp ?? '');
      const hasNewGenerated = Boolean(
        generatedIcp && generatedIcp !== icpData?.data
      );
      const shouldUpdate =
        !icpData?.isModified &&
        (hasNoUserContent || (hasNewGenerated && isStillShowingSavedIcp));

      if (shouldUpdate) {
        setIcpData({
          data: icpText,
          categories,
          isModified: false, // Never mark as modified on initialization
        });
      }
    } else {
      // Reset ICP data when drawer closes to ensure fresh initialization on next open
      setIcpData({
        data: '',
        categories: [],
        isModified: false,
      });
    }
  }, [
    isOpen,
    aiScoringData,
    generatedIcp,
    generatedCategories,
    icpData.data,
    icpData.isModified,
  ]);

  // Memoize attributes list ref instead of using useEffect
  const attributesListRefValue = useMemo(
    () => ({
      getAttributes: () => attributes,
      saveAttributes: saveAttributes,
      isLoading: attributesLoading,
      dirty: isDirty,
      isValid: isValid,
    }),
    [attributes, saveAttributes, attributesLoading, isDirty, isValid]
  );

  // Update ref when value changes (necessary for ref updates)
  useEffect(() => {
    attributesListRef.current = attributesListRefValue;
  }, [attributesListRefValue]);

  // Handle initial navigation on mount - only once if both ICP and attributes exist
  useEffect(() => {
    if (isOpen && !hasInitialNavigationRef.current && activeStep === STEP_ICP) {
      const hasExistingIcp = Boolean(aiScoringData?.icp || generatedIcp);
      const hasExistingAttributes = Boolean(
        aiScoringData?.attributes?.length > 0 ||
        (Array.isArray(generatedAttributes) && generatedAttributes.length > 0)
      );

      if (hasExistingIcp && hasExistingAttributes) {
        // move to step
        setActiveStep(STEP_ATTRIBUTES);
      }
      // Always set to true to prevent navigation during later steps in the process
      hasInitialNavigationRef.current = true;
    }
  }, [
    isOpen,
    activeStep,
    aiScoringData,
    generatedIcp,
    generatedAttributes,
    setActiveStep,
  ]);

  // Handle case when attribute generation fails - allow manual navigation after ICP is saved
  useEffect(() => {
    // If ICP is saved but not loading and we're still on step 1,
    // enable navigation to step 2 for manual attribute addition
    if (
      activeStep === STEP_ICP &&
      icpData?.data &&
      !icpData?.isModified &&
      !isIcpLoading &&
      (!generatedAttributes || generatedAttributes.length === 0)
    ) {
      // ICP is saved but no attributes generated - user can manually navigate to step 2
      // This is handled by the footer button logic and step navigation
    }
  }, [activeStep, icpData, isIcpLoading, generatedAttributes]);

  // Only the auto-reject settings changed (no attribute edits) — saving goes
  // straight through the settings endpoint, since the attribute save flow
  // would refuse to save unchanged attributes (ENG-3049 feedback: changing
  // just the score must be saveable).
  const isOnlyAutoRejectChanged =
    isAutoRejectOrEmailDirty &&
    !isDirty &&
    !hasUnsavedGeneratedAttributes &&
    !hasValidatedButNotSaved;

  // Create wrapped handlers that check shouldRetrigger flag
  const handleSaveWithModal = async () => {
    if (isOnlyAutoRejectChanged) {
      if (!validateEmailOrToast()) return;
      mutateAiScoringSettings({ autoReject, autoRejectThreshold });
      return;
    }
    if (shouldRetrigger) {
      setRetriggerModal({
        isOpen: true,
        actionType: 'save',
        loadingAction: null,
      });
    } else {
      if (!(await persistAutoRejectIfChanged())) return;
      handleSave();
    }
  };

  const handleSkipAndSaveWithModal = async () => {
    if (isOnlyAutoRejectChanged) {
      if (!validateEmailOrToast()) return;
      mutateAiScoringSettings({ autoReject, autoRejectThreshold });
      return;
    }
    if (shouldRetrigger) {
      setRetriggerModal({
        isOpen: true,
        actionType: 'skipAndSave',
        loadingAction: null,
      });
    } else {
      if (!(await persistAutoRejectIfChanged())) return;
      handleSkipAndSave();
    }
  };

  // Modal handlers
  const handleRetriggerModalClose = () => {
    setRetriggerModal({ isOpen: false, actionType: null, loadingAction: null });
  };

  const handleSaveWithoutRetrigger = async () => {
    let currentActionType: 'save' | 'skipAndSave' | null = null;

    setRetriggerModal((prev) => {
      currentActionType = prev.actionType;
      return {
        ...prev,
        loadingAction: 'saveWithoutRetrigger',
      };
    });

    try {
      if (await persistAutoRejectIfChanged()) {
        if (currentActionType === 'save') {
          await handleSave(false); // shouldRetrigger = false
        } else if (currentActionType === 'skipAndSave') {
          await handleSkipAndSave(false); // shouldRetrigger = false
        }
      }
    } finally {
      setRetriggerModal((prev) => ({
        ...prev,
        isOpen: false,
        loadingAction: null,
      }));
    }
  };

  const handleSaveAndRetrigger = async () => {
    let currentActionType: 'save' | 'skipAndSave' | null = null;

    setRetriggerModal((prev) => {
      currentActionType = prev.actionType;
      return {
        ...prev,
        loadingAction: 'saveAndRetrigger',
      };
    });

    try {
      if (await persistAutoRejectIfChanged()) {
        if (currentActionType === 'save') {
          await handleSave(true); // shouldRetrigger = true
        } else if (currentActionType === 'skipAndSave') {
          await handleSkipAndSave(true); // shouldRetrigger = true
        }
      }
    } finally {
      setRetriggerModal((prev) => ({
        ...prev,
        isOpen: false,
        loadingAction: null,
      }));
    }
  };

  if (!isOpen) return null;

  // Check if step 3 (Test AI scoring) should be enabled
  // Step 3 logic should match the footer "Next: Test AI scoring" button logic
  const isTestingScoringStepEnabled = () => {
    if (footerIsLoading) {
      return false;
    }

    // Helper to check if attributes have meaningful content (prevents "just typed a space" issue)
    const hasMeaningfulContent =
      attributes &&
      attributes.length > 0 &&
      attributes.filter((attr) => attr.description?.trim().length >= 10)
        .length >= 3;

    if (isEditMode) {
      // Edit mode: Different scenarios need different handling
      const hasUserModifications = isDirty && isValid && hasMeaningfulContent;
      const hasValidatedChanges = hasValidatedButNotSaved;
      const hasFreshlyGeneratedUnsaved = hasUnsavedGeneratedAttributes; // Now based on generatedAttributes presence

      const hasMeaningfulChanges =
        hasUserModifications || // User made manual changes
        hasValidatedChanges || // User validated but not saved
        hasFreshlyGeneratedUnsaved; // Freshly generated from ICP (not based on IDs)

      return hasMeaningfulChanges;
    } else {
      // Create mode: enabled when has meaningful content (not just basic isValid)
      return isValid && hasMeaningfulContent;
    }
  };

  // Calculate step enablement with debugging
  // Step 1 (ICP) should always be enabled unless we're actively testing AI scoring
  const stepIcpEnabled = !isAiTestLoading;
  const stepAttributesEnabled =
    !footerIsLoading &&
    !isIcpGenerating &&
    !generateAttributesMutation.isPending &&
    !isIcpLoading &&
    (icpData?.data?.trim().length || 0) >= ICP_MIN_LENGTH &&
    !isAiTestLoading;
  const stepTestingEnabled = isTestingScoringStepEnabled() && !isAiTestLoading;

  const steps: KStepItem[] = [
    {
      id: STEP_ICP,
      label: 'Define ICP',
      status: activeStep === STEP_ICP ? 'active' : 'completed',
      enabled: stepIcpEnabled,
      labelProps: {
        minWidth: 'fit-content',
      },
    },
    {
      id: STEP_ATTRIBUTES,
      label: 'Verify attributes',
      status:
        activeStep === STEP_ATTRIBUTES
          ? 'active'
          : activeStep > STEP_ATTRIBUTES
            ? 'completed'
            : 'pending',
      enabled: stepAttributesEnabled,
      labelProps: {
        minWidth: 'fit-content',
      },
    },
    {
      id: STEP_TESTING,
      label: 'Test Scoring agent',
      status: activeStep === STEP_TESTING ? 'active' : 'pending',
      enabled: stepTestingEnabled,
      labelProps: {
        minWidth: 'fit-content',
      },
    },
  ];

  const HeaderSection = () => {
    return (
      <Flex flexDir="column" gap="16px">
        <KText variant="heading_4">Set up Scoring agent</KText>
        <KSteps
          steps={steps}
          onSelect={({ step }) => handleStepChange(Number(step.id ?? 1))}
        />
      </Flex>
    );
  };

  const handleClose = () => {
    if (currentView === 'info') {
      if (isAutoRejectOrEmailDirty) {
        cancelBtnDisclosure.onOpen();
        return;
      }
      onClose?.();
      return;
    }
    cancelBtnDisclosure.onOpen();
  };
  const isInfoView = currentView === 'info';
  const isInfoSaving = updateAiScoringSettingsMutation.isPending;
  const shouldSkipFooter = isAiTestLoading || isIcpLoading;

  const InfoHeader = () => (
    <KText variant="heading_4">Edit Scoring agent</KText>
  );

  return (
    <KDrawer
      isOpen={isOpen}
      onClose={handleClose}
      variant="wide"
      KDrawerHeaderProps={{
        title: isInfoView ? InfoHeader() : HeaderSection(),
      }}
      bodyProps={{
        h: '100%',
        overflow: isInfoView ? 'auto' : 'hidden',
      }}
      skipFooter={shouldSkipFooter}
      footer={
        isInfoView ? (
          <Flex
            justifyContent="flex-end"
            w="100%"
            flexShrink={0}
            borderTop="1px solid"
            borderColor="secondary-100"
            p="12px"
            gap="8px"
          >
            <Button
              variant="secondary"
              onClick={handleInfoCancel}
              isDisabled={isInfoSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleInfoSave}
              isLoading={isInfoSaving}
              isDisabled={
                !isAutoRejectOrEmailDirty && (aiScoringData?.enabled ?? false)
              }
              loadingText="Saving..."
            >
              Save
            </Button>
          </Flex>
        ) : (
          <AiScoringDrawerFooter
            activeStep={activeStep}
            onCancel={handleCancel}
            onBack={handleBack}
            onSave={handleSaveWithModal}
            onSkipAndSave={handleSkipAndSaveWithModal}
            onVerifyIcp={handleVerifyIcp}
            onVerifyAttributes={handleVerifyAttributes}
            isDirty={isDirty}
            isAutoRejectDirty={isAutoRejectOrEmailDirty}
            isLoading={footerIsLoading || isInfoSaving}
            loadingKey={
              isInfoSaving
                ? activeStep === STEP_TESTING
                  ? 'save'
                  : 'skip'
                : footerLoadingKey
            }
            isValid={isValid}
            hasUnsavedGeneratedAttributes={hasUnsavedGeneratedAttributes}
            isEditMode={isEditMode}
            hasValidatedButNotSaved={hasValidatedButNotSaved}
            icpData={icpData}
            attributes={attributes}
            interactedAttributes={interactedAttributes}
            cancelBtnDisclosure={cancelBtnDisclosure}
          />
        )
      }
    >
      {/* Info view — shown when opened from AI Teammates screener card */}
      {isInfoView && (
        <ScreenerInfoContent
          attributes={(aiScoringData?.attributes as any[]) ?? []}
          autoReject={autoReject}
          autoRejectThreshold={autoRejectThreshold}
          onAutoRejectChange={setAutoReject}
          onThresholdChange={setAutoRejectThreshold}
          onEditAttributes={handleEditAttributes}
          isSaving={isInfoSaving}
          autoRejectEmailSlot={
            feAiAutoRejectEmail && autoReject ? (
              <AutoRejectEmailSection
                formRef={autoRejectEmailFormRef}
                initialValues={autoRejectEmailInitialValues}
                onDirtyChange={setAutoRejectEmailDirty}
              />
            ) : null
          }
        />
      )}

      {/* Setup view — standard ICP → Attributes → Testing flow */}
      <Box w="100%" h="100%" display={isInfoView ? 'none' : 'block'}>
        <Box
          w="100%"
          h="100%"
          display={activeStep === STEP_ICP ? 'block' : 'none'}
          overflow="hidden"
        >
          <AiICP
            jobId={jobData.id}
            isIcpLoading={isIcpLoading}
            attributes={attributes}
            // saveIcpMutation={saveIcpMutation} // Commented out - can be uncommented in future
            isIcpGenerating={isIcpGenerating}
            getIcpCategoriesMutation={getIcpCategoriesMutation}
            aiScoringData={aiScoringData}
            setActiveStep={setActiveStep}
            icpData={icpData}
            setIcpData={setIcpData}
            onVerifyIcp={handleVerifyIcp}
          />
        </Box>
        <Box
          w="100%"
          h="100%"
          display={activeStep === STEP_ATTRIBUTES ? 'block' : 'none'}
          overflow="hidden"
        >
          <AiCandidateAttributes
            aiScoringData={aiScoringData}
            // Props from centralized hook
            attributes={attributes}
            isLoading={attributesLoading}
            isValid={isValid}
            isDirty={isDirty}
            onAttributeChange={handleAttributeChange}
            onAttributeBlur={handleAttributeBlur}
            onAttributeDelete={handleAttributeDelete}
            onAttributeAdd={handleAttributeAdd}
            onAttributeReorder={handleAttributeReorder}
            onSaveAttributes={saveAttributes}
            footerIsLoading={footerIsLoading}
            setActiveStep={setActiveStep}
            // Props from ICP generation
            generatedAttributes={
              generatedAttributes ?? aiScoringData?.attributes
            }
            setValidatedButNotSaved={setValidatedButNotSaved}
            onAttributeAutoFocus={handleAttributeAutoFocus}
            // Auto-reject bar follows the attributes + Add attribute row
            // (ENG-3049 feedback: not pinned at the drawer's end); padding
            // matches the list header/footer rows
            afterListContent={
              feJobTeammates ? (
                <Box flexShrink={0} p="0 15.5px">
                  <AutoRejectSection
                    enabled={autoReject}
                    onToggle={setAutoReject}
                    threshold={autoRejectThreshold}
                    onThresholdChange={setAutoRejectThreshold}
                  />
                  {autoReject && feAiAutoRejectEmail && (
                    <AutoRejectEmailSection
                      formRef={autoRejectEmailFormRef}
                      initialValues={autoRejectEmailInitialValues}
                      onDirtyChange={setAutoRejectEmailDirty}
                    />
                  )}
                </Box>
              ) : undefined
            }
          />
        </Box>
        <Box
          style={{ display: activeStep === STEP_TESTING ? 'block' : 'none' }}
          w="100%"
          h="100%"
        >
          <AiScoreTesting
            jobId={jobData.id}
            autoStartWithSession={
              activeStep === STEP_TESTING &&
              (hasTestSession || shouldRerunAiTest)
            }
            shouldRerunAiTest={shouldRerunAiTest}
            onSessionChange={(session, sessionKeyValue) => {
              // Store AI scoring test session info for auto-generation and cleanup
              setTestSessionKey(sessionKeyValue);

              // Only reset the rerun flag if we're not in a reset-to-test-mode scenario
              // We should only reset when the test actually starts, not when mode changes
              // The flag will be reset by the AiScoreTesting component when it actually starts the test
            }}
            onTestStart={() => {
              // Reset the rerun flag when the test actually starts
              if (shouldRerunAiTest) {
                setShouldRerunAiTest(false);
              }
            }}
            onLoadingChange={(loading) => {
              setIsAiTestLoading(loading);
            }}
          />
        </Box>
      </Box>

      {/* Cancel confirmation modal - moved here so it works even when footer is skipped */}
      {cancelBtnDisclosure.isOpen &&
        (() => {
          // Info view: check info-specific dirty state
          if (isInfoView) {
            if (!isAutoRejectOrEmailDirty) {
              onClose?.();
              cancelBtnDisclosure.onClose();
              return null;
            }
            return (
              <ModalWithForm
                withForm={false}
                isOpen={cancelBtnDisclosure.isOpen}
                onClose={() => cancelBtnDisclosure.onClose()}
                headerText="Unsaved changes will be discarded"
                subHeaderText="Your changes to Scoring agent settings will be lost. Are you sure you want to continue?"
                primaryCtaText="Confirm"
                handleSubmit={() => {
                  onClose?.();
                  cancelBtnDisclosure.onClose();
                }}
              />
            );
          }

          // Setup view: existing logic
          const hasUnsavedIcp = icpData?.isModified || false;
          const hasUnsavedAttributes =
            isDirty || hasUnsavedGeneratedAttributes || hasValidatedButNotSaved;
          const hasUnsavedGeneratedIcp = Boolean(generatedIcp);
          const hasAnyUnsavedChanges =
            hasUnsavedIcp ||
            hasUnsavedAttributes ||
            hasUnsavedGeneratedIcp ||
            isAutoRejectOrEmailDirty;
          const hasOngoingOperation =
            footerIsLoading ||
            isIcpLoading ||
            isAiTestLoading ||
            attributesLoading;

          if (!hasAnyUnsavedChanges && !hasOngoingOperation) {
            handleCancel();
            cancelBtnDisclosure.onClose();
            return null;
          }

          return (
            <ModalWithForm
              withForm={false}
              isOpen={cancelBtnDisclosure.isOpen}
              onClose={() => {
                cancelBtnDisclosure.onClose();
              }}
              headerText="Unsaved changes will be discarded"
              subHeaderText="Any unsaved candidate attribute changes will be discarded. Are you sure you want to continue?"
              primaryCtaText="Confirm"
              handleSubmit={() => {
                handleCancel();
              }}
            />
          );
        })()}

      {/* Retrigger confirmation modal */}
      {retriggerModal.isOpen && (
        <AiScoringRerunModal
          isOpen={retriggerModal.isOpen}
          onClose={handleRetriggerModalClose}
          onSaveWithoutRetrigger={handleSaveWithoutRetrigger}
          onSaveAndRetrigger={handleSaveAndRetrigger}
          isLoading={footerIsLoading || retriggerModal.loadingAction !== null}
          loadingAction={retriggerModal.loadingAction}
        />
      )}
    </KDrawer>
  );
};
